/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Shared "auto-parse & mapping-suggestion" utility (epic #856 — #859 phase 1).
 *
 * A single, framework-agnostic engine that turns a **sample** — an OpenRTB body,
 * a demand-source endpoint URL, or an inbound pixel URL — into a normalized list
 * of {@link MappingSuggestion}s. Each leaf/param is matched against the shared
 * bidBrain macro vocabulary (`MACRO_DEFAULTS`, aligned with the driver-side
 * canonical context resolver) to suggest a mapping: a `{macro}` token, a context
 * path, or a static value fallback.
 *
 * Consumers (all authoring surfaces, so suggestions stay consistent):
 * - ORTB raw-body editor (#858) — parse a pasted BidRequest → per-leaf mappings
 * - VAST / request-params editor — parse an endpoint → per-param mappings
 * - Pixel extraction editor (#527) — parse a sample pixel → inbound mappings
 *
 * This is phase 1 ("extract & normalize the matching logic"). The shared mapping
 * UX component (phase 2) and named/shared-expression suggestions (phases 3-4)
 * build on top of the same `suggestMapping` primitive.
 */

// One matcher, not two: this module's private `matchMacro` had the correct
// whole-token rule while `getMacroDefaultPath` kept a raw substring fallback, and
// the row builder was wired to the wrong one (#1003). The rule now lives beside
// the vocabulary it matches against.
import { matchMacroName as matchMacro } from '../components/shared/inputs/data-model-schema';
import { canonicalizeInboundTarget } from '@destination-field';
import { catalogEntryFor } from '@field-catalog';
import { describeIdentifyingShape, looksIdentifying, sampleLiteralPolicy } from '@sample-literal-policy';
import type { DetectionConfidence, SampleLiteral, ValueOrigin } from '@value-provenance';
import type { MappingEntry, MappingScalarType } from '../types/mapping';
import { diagnoseTypographicQuotes } from '../components/shared/editors/json-normalize';

/** How a sampled leaf/param is suggested to be mapped. */
export type SuggestionKind = 'macro' | 'static';

/** Origin of a sampled leaf — lets the UI group suggestions. */
export type SuggestionSource = 'body' | 'query' | 'pixel';

/** Confidence in the suggested mapping. */
export type SuggestionConfidence = 'high' | 'medium' | 'low';

export interface MappingSuggestion {
  /** Full key/path of the leaf in the sample (e.g. `device.w`, `us_privacy`). */
  key: string;
  /** Display label — the last path segment. */
  label: string;
  /** The literal value found in the sample. */
  sampleValue: unknown;
  /** Where the leaf came from. */
  source: SuggestionSource;
  /** Suggested mapping kind. */
  kind: SuggestionKind;
  /** For `macro`: the token form to insert, e.g. `{us_privacy}`. */
  macroToken?: string;
  /** For `macro`: the equivalent dynamic context path, e.g. `session.privacy.usPrivacy`. */
  suggestedPath?: string;
  /** Confidence of the suggestion. */
  confidence: SuggestionConfidence;
  /** Short human explanation of why this mapping was suggested. */
  reason: string;
  /**
   * **A value shaped like a viewer's data, under a name nothing recognises** (`#1016`).
   *
   * Present only on a `static` suggestion: the shape is a reason to warn, never a reason to map
   * (Todd, 2026-09-15). `reason` then carries the parser's own sentence, and `macro` is what the
   * operator could map it to if the value really is per-viewer.
   */
  perViewerShape?: { macro: string; why: string };
}

export interface ParseSampleResult {
  suggestions: MappingSuggestion[];
  /** Set when the sample could not be parsed. */
  error?: string;
}

/** The last dot-separated segment of a key/path. */
function lastSegment(key: string): string {
  const parts = key.split('.');
  return parts[parts.length - 1] || key;
}

/**
 * The shared per-leaf matcher. Given a key (and optional sample value), suggest
 * the best mapping. Used identically for body leaves, query params, and pixels.
 */
export function suggestMapping(key: string, sampleValue: unknown, source: SuggestionSource): MappingSuggestion {
  const label = lastSegment(key);
  const base: MappingSuggestion = {
    key,
    label,
    sampleValue,
    source,
    kind: 'static',
    confidence: 'low',
    reason: 'No known field matched; suggested as a static value.',
  };

  // 1) Key-based match against the shared macro vocabulary (highest signal).
  const byKey = matchMacro(label);
  if (byKey) {
    return {
      ...base,
      kind: 'macro',
      macroToken: `{${byKey.macro}}`,
      suggestedPath: byKey.path,
      confidence: byKey.exact ? 'high' : 'medium',
      reason: byKey.exact
        ? `Key "${label}" matches the "${byKey.macro}" macro.`
        : `Key "${label}" looks related to the "${byKey.macro}" macro.`,
    };
  }

  // 2) **A value's shape warns, it never converts** (`#1016`, Todd 2026-09-15). This mapped an IPv4
  // under an unrecognised key to `{ip}` and a UUID to `{ifa}` — the weakest evidence available for the
  // highest-consequence targets in the vocabulary — while the tag parser, answering the same question
  // beside it, only ever warned. The shapes and the sentence are the parser's now, from one module.
  const shape = typeof sampleValue === 'string' ? looksIdentifying(sampleValue) : null;
  if (shape) {
    const macro = matchMacro(shape.macro)?.macro ?? shape.macro;
    return {
      ...base,
      perViewerShape: { macro, why: shape.why },
      reason: describeIdentifyingShape(label, shape.why, macro),
    };
  }

  return base;
}

/**
 * **The origin a detected row is stamped with, from a match and the sample it was found in**
 * (`#1001`, `L3`).
 *
 * One helper for every console intake, because what a guess records is the same everywhere: the
 * detector's grade, and what Revert would return to — the literal, or, under the same veto that
 * decides what may be stored as a default (`#952`, `#1188`), the reason it was not kept. A row the
 * detector did not map is a detection with no guess in it.
 */
export function detectedOriginForMatch(
  macroToken: string | undefined,
  confidence: DetectionConfidence,
  sampleValue: unknown,
): ValueOrigin {
  if (!macroToken) return { kind: 'detected' };
  const scalar =
    typeof sampleValue === 'string' || typeof sampleValue === 'number' || typeof sampleValue === 'boolean'
      ? sampleValue
      : undefined;
  const outcome = sampleLiteralPolicy(catalogEntryFor(macroToken.replace(/[{}]/g, '')));
  const sample: SampleLiteral | undefined =
    scalar === undefined || scalar === ''
      ? undefined
      : outcome !== 'retain'
        ? { withheld: outcome }
        : { value: scalar };
  return { kind: 'detected', confidence, ...(sample ? { sample } : {}) };
}

/** {@link detectedOriginForMatch} for a {@link MappingSuggestion}. */
export function detectedOriginFor(suggestion: MappingSuggestion): ValueOrigin {
  return detectedOriginForMatch(
    suggestion.kind === 'macro' ? suggestion.macroToken : undefined,
    suggestion.confidence,
    suggestion.sampleValue,
  );
}

/**
 * Recursively flatten an object into `{ path, value }` leaves. Arrays and scalars
 * are treated as leaves (an array value is not descended into — it maps as one field).
 */
function flattenLeaves(obj: unknown, prefix: string, out: Array<{ path: string; value: unknown }>): void {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    if (prefix) out.push({ path: prefix, value: obj });
    return;
  }
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      flattenLeaves(v, path, out);
    } else {
      out.push({ path, value: v });
    }
  }
}

/**
 * Parse a sample OpenRTB `BidRequest` (JSON string or object) into per-leaf
 * mapping suggestions.
 */
export function parseSampleOrtbBody(input: string | Record<string, unknown>): ParseSampleResult {
  let parsed: unknown;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return { suggestions: [], error: 'Sample body is empty.' };
    try {
      parsed = JSON.parse(trimmed);
    } catch (err) {
      return { suggestions: [], error: `Invalid JSON: ${err instanceof Error ? err.message : String(err)}` };
    }
  } else {
    parsed = input;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { suggestions: [], error: 'Sample body must be a JSON object.' };
  }

  const leaves: Array<{ path: string; value: unknown }> = [];
  flattenLeaves(parsed, '', leaves);
  return { suggestions: leaves.map((l) => suggestMapping(l.path, l.value, 'body')) };
}

/**
 * Extract the query parameters from a URL and suggest a mapping per parameter.
 * `source` distinguishes an endpoint (`query`, default) from an inbound pixel
 * (`pixel`, staging #527) — the matching logic is identical.
 */
export function parseSampleUrl(url: string, source: 'query' | 'pixel' = 'query'): ParseSampleResult {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return { suggestions: [], error: 'Sample URL is empty.' };

  let search: URLSearchParams;
  try {
    // Support bare `?a=1&b=2`, full URLs, and macro-laden templates.
    const qIndex = trimmed.indexOf('?');
    const query = qIndex >= 0 ? trimmed.slice(qIndex + 1) : trimmed.includes('=') ? trimmed : '';
    search = new URLSearchParams(query);
  } catch (err) {
    return { suggestions: [], error: `Could not parse URL: ${err instanceof Error ? err.message : String(err)}` };
  }

  const suggestions: MappingSuggestion[] = [];
  for (const [name, value] of search.entries()) {
    suggestions.push(suggestMapping(name, value, source));
  }
  if (suggestions.length === 0) {
    return { suggestions: [], error: 'No query parameters found in the sample URL.' };
  }
  return { suggestions };
}

/** Convenience wrapper: parse a sample inbound pixel URL (staging #527). */
export function parseSamplePixel(url: string): ParseSampleResult {
  return parseSampleUrl(url, 'pixel');
}

// ---------------------------------------------------------------------------
// Inbound response mapping (#866) — parse a captured ad/bid response into
// suggested MappingEntry[] (the mirror of parseSampleOrtbBody, store-side).
// ---------------------------------------------------------------------------

/** Standard ORTB bid path per optimizer target, used to seed suggestions. */
const ORTB_STANDARD_TARGETS: ReadonlyArray<{ target: string; rawPath: string; asType: MappingScalarType }> = [
  { target: 'bid', rawPath: 'price', asType: 'number' },
  { target: 'adId', rawPath: 'crid', asType: 'string' },
  { target: 'adDomain', rawPath: 'adomain.0', asType: 'string' },
  { target: 'iabCategory', rawPath: 'cat.0', asType: 'string' },
  { target: 'winNoticeUrl', rawPath: 'nurl', asType: 'string' },
  { target: 'billingNoticeUrl', rawPath: 'burl', asType: 'string' },
];

function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, key) => {
    if (cur == null || typeof cur !== 'object') return undefined;
    return (cur as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Stamp `provenance: 'detected'` on every entry read out of a sample (R4/R4b).
 *
 * Applied at the **return boundary**, not at each `entries.push(...)`, on purpose:
 * `MappingEntry.provenance` is optional, so a push site that forgets it is
 * type-clean, test-clean and renders a bare row — `ProvenanceChip` returns `null`
 * for `undefined`. Every entry this module produces was read out of a pasted or
 * fetched response, so the stamp belongs where that fact is structurally true
 * rather than at each site that has to remember it.
 *
 * This mirrors `libs/adapters/ad-tag.adapter.ts`, which has stamped `'detected'`
 * on rows read out of a pasted tag since R4b. The console side had the vocabulary
 * and the chip and wrote the value nowhere, so the one case carrying the most
 * information — a row derived from the exchange's *real* response via auto-fetch
 * (#866) — was indistinguishable from something the operator typed by hand.
 */
function detected(entries: MappingEntry[]): MappingEntry[] {
  // No confidence and no sample: each entry is a standard target found by name in a fixed table, so
  // there is no guess to mark and nothing to revert (`#1001`).
  return entries.map((e) => (e.provenance ? e : { ...e, provenance: { kind: 'detected' as const } }));
}

export interface ParseResponseResult {
  entries: MappingEntry[];
  error?: string;
  /**
   * Smart-quote-normalized input, present only when normalizing it would actually
   * parse. Lets the caller offer a one-click repair instead of only naming the cause.
   */
  quoteFix?: string;
}

/**
 * Parse a captured demand-source response (raw ORTB JSON / VAST XML) into
 * suggested inbound `MappingEntry[]` — one per optimizer-critical field that is
 * actually present in the sample. Feeds "paste sample & parse" and the #867
 * auto-fetch flow.
 */
export function parseSampleResponse(
  input: string,
  sourceDomain: 'ortb-bid-response' | 'vast-ad-response',
): ParseResponseResult {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return { entries: [], error: 'Sample response is empty.' };

  if (sourceDomain === 'ortb-bid-response') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (err) {
      const base = `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`;
      // A pasted sample almost always came through a doc, notes app or chat client,
      // any of which may have rewritten the straight quotes. Diagnose and offer the
      // repair rather than handing back the parser's message (R3, and #943 — which
      // covered the ORTB body paste but not this one).
      const { fixable, normalized } = diagnoseTypographicQuotes(trimmed);
      if (fixable) {
        return {
          entries: [],
          error: `${base} — this looks like curly quotes (“ ”); JSON needs straight quotes (").`,
          quoteFix: normalized,
        };
      }
      return { entries: [], error: base };
    }
    // Accept a full bid response or a bare bid object.
    const bid =
      (getByPath(parsed, 'seatbid.0.bid.0') as Record<string, unknown> | undefined) ??
      (parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined);
    if (!bid) return { entries: [], error: 'Could not find a bid object (seatbid[0].bid[0]).' };

    const entries: MappingEntry[] = [];
    for (const m of ORTB_STANDARD_TARGETS) {
      if (getByPath(bid, m.rawPath) !== undefined) {
        entries.push({
          // **A write emits one spelling** (`X49`, `X3`). The stored form is namespaced.
          target: ['ad', m.target],
          source: { type: 'calculated', path: `response.${m.rawPath}` },
          asType: m.asType,
        });
      }
    }
    if (entries.length === 0) return { entries: [], error: 'No standard bid fields found in the sample.' };
    return { entries: detected(entries) };
  }

  // VAST — the driver parses the ad XML into JSON, so suggest JSON-path sources
  // (matching the default document). Detect element presence in the raw sample.
  const entries: MappingEntry[] = [];
  if (/<Pricing[\s>]/i.test(trimmed)) {
    entries.push({
      target: ['ad', 'bid'],
      source: { type: 'calculated', path: 'response.InLine.Pricing.#text' },
      asType: 'number',
    });
  }
  if (/<Advertiser[\s>]/i.test(trimmed)) {
    entries.push({
      target: ['ad', 'adDomain'],
      source: { type: 'calculated', path: 'response.InLine.Advertiser' },
      asType: 'string',
    });
  }
  if (/<Category[\s>]/i.test(trimmed)) {
    entries.push({
      target: ['ad', 'iabCategory'],
      source: { type: 'calculated', path: 'response.InLine.Category' },
      asType: 'string',
    });
  }
  if (entries.length === 0) {
    return { entries: [], error: 'No mappable VAST fields (Pricing/Advertiser/Category) found in the sample.' };
  }
  return { entries: detected(entries) };
}

/** What applying a set of suggested entries would do to the configured ones. */
export interface MappingEntryDiff {
  /** Suggestions for targets not configured yet. */
  added: MappingEntry[];
  /** Suggestions whose source differs from the configured entry for that target. */
  updated: MappingEntry[];
  /** Suggestions that restate what is already configured. */
  unchanged: MappingEntry[];
}

/**
 * Diff suggested mappings against the configured ones, by target. Used to tell
 * the operator what a parsed sample actually changed — re-parsing the same
 * response must not claim edits it didn't make.
 */
export function diffMappingEntries(existing: MappingEntry[], suggested: MappingEntry[]): MappingEntryDiff {
  /**
   * **Keyed on the canonical target** (`X49`, `#1232`).
   *
   * A stored entry holds whatever spelling it was authored in and a suggestion now emits the
   * namespaced one, so a raw key would report every pre-`X49` mapping as **added** — re-parsing the
   * same response would propose `ad.bid` beside the operator's existing `bid`, which is two rows for
   * one field and the `#1013` shape (*a button proposing what already happens*) with a rename behind
   * it. Same fix as `targetKey` in the driver's merge, and for the same reason.
   */
  const key = (e: MappingEntry) => canonicalizeInboundTarget(e.target).join('.');
  const byTarget = new Map(existing.map((e) => [key(e), e]));
  const diff: MappingEntryDiff = { added: [], updated: [], unchanged: [] };
  for (const entry of suggested) {
    const prev = byTarget.get(key(entry));
    if (!prev) {
      diff.added.push(entry);
    } else if (JSON.stringify(prev.source) !== JSON.stringify(entry.source) || prev.asType !== entry.asType) {
      diff.updated.push(entry);
    } else {
      diff.unchanged.push(entry);
    }
  }
  return diff;
}
